import type { Vec2 } from '../../geo/types';
import type { Lot } from './types';
import { lotFrame, lotSides } from './lotFrame';
import { obbToAABB } from './lotObb';
import { createCellGrid, type CellGrid } from './cellGrid';

/**
 * PLAN Lots: a house's plot (erf) runs straight back from the house to its back line: the water, a
 * street, a reserved footprint, the side of another house, or halfway to a house standing back to
 * back with it. No plot reaches further than `reach` from its street front: half the depth of the
 * block between the Warmoesstraat and the Oudezijds Voorburgwal, 74 to 83 m between the street
 * fronts, whose lots met back to back (Nikkels 2013; Van Deventer). A plot runs on to an unquayed
 * bank within `bankReach`: the Warmoesstraat's lots ran through to the Damrak (Cornelis Anthonisz's
 * plan of 1544), up to 50 m from the street front. A ray marches in `step`s; a house whose facing
 * turns from this one's by more than `backToBack` (a cosine) stands back to back with it. Where a
 * plot's sides meet a bank, halving the last step `bisect` times finds the bank to within 2 cm.
 */
export const PLOT = { reach: 40, bankReach: 60, step: 0.5, backToBack: -0.5, bisect: 5 } as const;

export interface PlotGround {
  readonly buildable: (p: Vec2) => boolean;
  readonly reserved: (p: Vec2) => boolean;
  /** Whether a house stands in the walled city, where the ground behind the houses was built on. */
  readonly walled: (p: Vec2) => boolean;
  readonly water: (p: Vec2) => boolean;
}

/** Where a bank plot's two sides meet the water, named as seen from its street. */
export interface BankLine {
  readonly left: Vec2;
  readonly right: Vec2;
}

export interface Plot {
  readonly lot: Lot;
  /** Free ground behind the house to the back line, along the house's depth. */
  readonly depth: number;
  /** Whether the back line is a bank: water, with no street or quay between. */
  readonly bank: boolean;
  /** The line a rear wall on the water stands on; null unless both sides of a bank plot end on the water. */
  readonly bankLine: BankLine | null;
}

function inside(lot: Lot, p: Vec2): boolean {
  const { center, axisU, halfU, halfV } = lot.obb;
  const dx = p[0] - center[0];
  const dy = p[1] - center[1];
  return (
    Math.abs(dx * axisU[0] + dy * axisU[1]) <= halfU &&
    Math.abs(-dx * axisU[1] + dy * axisU[0]) <= halfV
  );
}

function houseAt(grid: CellGrid<Lot>, p: Vec2, self: Lot): Lot | null {
  for (const other of grid.at(p[0], p[1])) if (other !== self && inside(other, p)) return other;
  return null;
}

type PlotEnd = Pick<Plot, 'depth' | 'bank'>;

/**
 * Free distance along the ray from `origin` running `dir`, no further than `limit`, or than
 * `bankLimit` where water ends it.
 */
function rayEnd(
  origin: Vec2,
  dir: Vec2,
  limits: { readonly plot: number; readonly bank: number },
  self: Lot,
  ground: PlotGround,
  grid: CellGrid<Lot>
): PlotEnd {
  for (let t = PLOT.step / 2; t < limits.bank; t += PLOT.step) {
    const p: Vec2 = [origin[0] + dir[0] * t, origin[1] + dir[1] * t];
    const edge = Math.max(0, t - PLOT.step / 2);
    if (!ground.buildable(p) || ground.reserved(p)) {
      const bank = !ground.reserved(p) && ground.water(p);
      return { depth: bank ? edge : Math.min(edge, limits.plot), bank };
    }
    const other = houseAt(grid, p, self);
    if (other) {
      const facing = other.facing[0] * -dir[0] + other.facing[1] * -dir[1];
      return { depth: Math.min(facing < PLOT.backToBack ? t / 2 : edge, limits.plot), bank: false };
    }
  }
  return { depth: limits.plot, bank: false };
}

function along(p: Vec2, dir: Vec2, t: number): Vec2 {
  return [p[0] + dir[0] * t, p[1] + dir[1] * t];
}

function dry(ground: PlotGround, p: Vec2): boolean {
  return ground.buildable(p) && !ground.reserved(p);
}

/* The last dry point on a side of the plot before the water; null where the side ends otherwise. */
function sideBank(from: Vec2, dir: Vec2, limit: number, ground: PlotGround): Vec2 | null {
  for (let t = PLOT.step / 2; t < limit; t += PLOT.step) {
    const p = along(from, dir, t);
    if (dry(ground, p)) continue;
    if (ground.reserved(p) || !ground.water(p)) return null;
    let [land, water] = [Math.max(0, t - PLOT.step), t];
    for (let k = 0; k < PLOT.bisect; k++) {
      const mid = (land + water) / 2;
      if (dry(ground, along(from, dir, mid))) land = mid;
      else water = mid;
    }
    return along(from, dir, land);
  }
  return null;
}

function bankLineOf(lot: Lot, dir: Vec2, limit: number, ground: PlotGround): BankLine | null {
  const { left, right } = lotSides(lot);
  const leftEnd = sideBank(left.to, dir, limit, ground);
  const rightEnd = leftEnd && sideBank(right.to, dir, limit, ground);
  return leftEnd && rightEnd ? { left: leftEnd, right: rightEnd } : null;
}

/* The centre line of the plot decides: a plot fanning out on a bend still has its middle to build on. */
function plotOf(lot: Lot, ground: PlotGround, grid: CellGrid<Lot>): Plot {
  const { frontMid, facing, depth } = lotFrame(lot);
  const dir: Vec2 = [-facing[0], -facing[1]];
  const back = along(frontMid, dir, depth);
  const limits = {
    plot: Math.max(0, PLOT.reach - depth),
    bank: Math.max(0, PLOT.bankReach - depth),
  };
  const end = rayEnd(back, dir, limits, lot, ground, grid);
  return { lot, ...end, bankLine: end.bank ? bankLineOf(lot, dir, limits.bank, ground) : null };
}

/** The plot behind every house in the walled city, in the order the houses were planned; every house bounds them. */
/* A ray asks for the house at every step; cells about a house wide hold few houses to ask. */
const HOUSE_CELL = 6;

export function planPlots(houses: readonly Lot[], ground: PlotGround): readonly Plot[] {
  const grid = createCellGrid<Lot>(HOUSE_CELL);
  for (const house of houses) grid.insert(house, obbToAABB(house.obb));
  return houses
    .filter((house) => ground.walled(house.obb.center))
    .map((house) => plotOf(house, ground, grid));
}
